import React, {useCallback, useMemo, useState} from 'react';

import {TemplateType} from 'src/openapi/admin/models/TemplateType';

import {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Tooltip} from '@pb/kit-component-popups';


import {CopiedTemplateRow} from './copied-template-row';
import {TemplateRow} from './template-row';

import {
    editSearchColumns,
    editSearchColumnsActiveLayoutButtons,
    editSearchColumnsActiveLayoutLabel,
    editSearchColumnsActiveTemplateContainer,
    editSearchColumnsLayoutsHeader,
} from './edit-search-columns.module.styl';


import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

type Props = {
    userId: number,
    activeTemplate: UserSearchTemplate,
    templates: UserSearchTemplate[],
    setActiveTemplate: (template: UserSearchTemplate) => void,
    openEditActiveTemplate: () => void,
    openAddModal: () => void,
    openSortingModal: () => void,
    openRemoveTemplate: (template: UserSearchTemplate) => void,
    onRenameDone: () => void,
    onRenameFail: (error: string) => void,
    onRenameCancel: () => void,
    onCopyDone: (newLayout: UserSearchTemplate) => void,
    onCopyFail: (error: string) => void,
    onCopyCancel: () => void,
};

export const EditSearchColumnsBody = (props: Props) => {
    const {
        userId,
        templates,
        openAddModal,
        activeTemplate,
        setActiveTemplate,
        openSortingModal,
        openEditActiveTemplate,
        openRemoveTemplate,
        onRenameDone,
        onRenameFail,
        onRenameCancel,
        onCopyFail,
        onCopyDone,
        onCopyCancel,
    } = props;

    const [hoveredTemplateId, setHoveredTemplateId] = useState<number | null>(null);
    const [busyTemplateId, setBusyTemplateId] = useState<number | null>(null);

    const [copiedTemplate, setCopiedTemplate] = useState<UserSearchTemplate | null>(null);

    const updateActiveTemplate = useCallback(async (template: UserSearchTemplate) => {
        setBusyTemplateId(template.templateId);
        await setActiveTemplate(template);
        setBusyTemplateId(null);
    }, [setActiveTemplate, setBusyTemplateId]);

    const doneCopy = useCallback((newLayout: UserSearchTemplate) => {
        setCopiedTemplate(null);
        onCopyDone(newLayout);
    }, [onCopyDone]);

    const cancelCopy = useCallback(() => {
        setCopiedTemplate(null);
        onCopyCancel();
    }, [onCopyCancel]);

    const canAddLayout = useMemo(
        () => templates.filter(({searchType}) => activeTemplate.searchType === searchType).length < 10,
        [templates, activeTemplate],
    );

    const hasCustomLayout = useMemo(
        () => templates.some(t => t.searchType === activeTemplate.searchType &&
            t.templateType !== TemplateType.DEFAULT),
        [templates, activeTemplate.searchType],
    );

    const editColumnsDisabled = activeTemplate.templateType === TemplateType.DEFAULT && hasCustomLayout;

    return (
        <section className={editSearchColumns}>
            <section className={editSearchColumnsActiveTemplateContainer}>
                <div className={editSearchColumnsActiveLayoutLabel}>
                    <Label>Active layout</Label>
                </div>
                <Group as="section" direction="v" alignItems="start" className={editSearchColumnsActiveLayoutButtons}>
                    <FlatButton
                        disabled={editColumnsDisabled}
                        onClick={openEditActiveTemplate}>
                        <b>Edit Columns</b>
                    </FlatButton>
                    <FlatButton
                        disabled={activeTemplate.templateType === TemplateType.DEFAULT}
                        onClick={openSortingModal}>
                        <b>Edit Sorting</b>
                    </FlatButton>
                </Group>
            </section>
            <section style={{width: 280}}>
                <Group as="header" justify="space-between" className={editSearchColumnsLayoutsHeader}>
                    <Label>Layouts</Label>
                    {canAddLayout ? (
                        <FlatButton startIcon={<Icon type={Icons.PLUS}/>} onClick={openAddModal}>
                            <b>Add Layout</b>
                        </FlatButton>
                    ) : (
                        <Tooltip
                            anchor={({anchorRef, getAnchorProps}) => (
                                <FlatButton
                                    ref={anchorRef}
                                    {...getAnchorProps()}
                                    disabled={true}
                                    startIcon={<Icon type={Icons.PLUS}/>}>
                                    <b>Add Layout</b>
                                </FlatButton>
                            )}>
                            You cannot have more than 10 layouts
                        </Tooltip>
                    )}
                </Group>
                <div>
                    {templates
                        .filter(template => template.searchType === activeTemplate.searchType)
                        .sort((a, b) => a.templateName
                            .localeCompare(b.templateName, undefined, {numeric: true, sensitivity: 'base'}),
                        )
                        .sort((a, b) => b.templateType.localeCompare(a.templateType))
                        .map(template => (
                            <TemplateRow
                                busy={busyTemplateId === template.templateId}
                                canCopy={canAddLayout}
                                key={template.templateId}
                                template={template}
                                templates={templates}
                                setActiveTemplate={updateActiveTemplate}
                                hoveredTemplateId={hoveredTemplateId}
                                setHoveredTemplateId={setHoveredTemplateId}
                                onRemoveTemplate={openRemoveTemplate}
                                onRenameDone={onRenameDone}
                                onRenameFail={onRenameFail}
                                onRenameCancel={onRenameCancel}
                                copyTemplate={setCopiedTemplate}
                            />
                        ))}
                    {copiedTemplate ? (
                        <CopiedTemplateRow
                            userId={userId}
                            template={copiedTemplate}
                            templates={templates}
                            onCopyDone={doneCopy}
                            onCopyFail={onCopyFail}
                            onCopyCancel={cancelCopy}
                        />
                    ) : null}
                </div>
            </section>
        </section>
    );
};
